import {CreateIssueQuery} from '@github-ui/server-persisted-queries'
import type {CreateIssueQueryQuery} from '@github-ui/server-persisted-queries/types/CreateIssueQuery'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {RepositoryNode} from './create-issue-query-types'

type TemplateResponse = {
  data?: Pick<CreateIssueQueryQuery, 'node'>
  errors?: readonly unknown[]
}

// Match the five-minute issue-create repository cache window so recent template selections are reused.
export const TEMPLATE_STALE_TIME = 5 * 60 * 1000

export function templateQueryKey(owner: string, repo: string, filename: string) {
  return ['issue-create', 'template', {owner, repo, filename}] as const
}

export async function fetchTemplate(
  owner: string,
  repo: string,
  filename: string,
  signal?: AbortSignal,
): Promise<RepositoryNode | null> {
  const search = new URLSearchParams({queryId: CreateIssueQuery, filename})
  const response = await verifiedFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/new/template?${search}`,
    {signal},
  )
  if (!response.ok) {
    throw new Error(`CreateIssueQuery fetch failed (${response.status})`)
  }

  const json = (await response.json()) as TemplateResponse
  if (json.errors?.length || !json.data) {
    throw new Error('CreateIssueQuery returned errors')
  }

  const node = json.data.node
  if (!node) {
    return null
  }
  if (node.__typename !== 'Repository') {
    throw new Error('CreateIssueQuery did not return a repository')
  }

  return node
}

type LatestTemplateRequest = {current: number}

export async function runLatestTemplateRequest<T>(
  latestRequest: LatestTemplateRequest,
  request: () => Promise<T>,
  applyResult: (result: T) => void,
  reportError: (error: unknown) => void,
): Promise<void> {
  const requestId = ++latestRequest.current

  try {
    const result = await request()
    if (requestId === latestRequest.current) {
      applyResult(result)
    }
  } catch (error) {
    if (requestId === latestRequest.current) {
      reportError(error)
    }
  }
}
